import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  Input
} from '@angular/core';
import { FastService } from 'src/app/core/services/fast.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-fast-multichannel',
  templateUrl: './storm-player.component.html',
  styleUrls: ['./storm-player.component.scss']
})
export class FastMultichannelComponent implements OnInit, OnDestroy {

  @ViewChild('videoElement', { static: true })
  videoElement!: ElementRef<HTMLVideoElement>;

  private destroy$ = new Subject<void>();
  private player: any;
  private epgReloadTimer: any;
  private progressTimer: any;

  @Input() controls = true;

  channels: any[] = [];
  activeChannel: any;
  currentTime: Date = new Date();
  networkLogo: string = "https://stageupload.bigbee.ai/org_2122/thumbnail/1767898617339.png";

  constructor(
    private fastService: FastService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const organizationId = this.route.snapshot.queryParamMap.get('organization_id');
    if (organizationId) {
      const orgId = parseInt(organizationId);
      if (!isNaN(orgId)) {
        this.fastService.setOrganizationId(orgId);
      }
    }

    const showferId = this.route.snapshot.queryParamMap.get('showfer_id');
    if (showferId) {
      const sfId = parseInt(showferId);
      if (!isNaN(sfId)) {
        this.fastService.setShowferOrgId(sfId);
      }
    }

    // Update clock every minute
    setInterval(() => { this.currentTime = new Date(); }, 60000);

    // Refresh progress/minsLeft every 30 s — avoids live Date.now() in template
    this.progressTimer = setInterval(() => this.refreshProgress(), 30000);

    this.loadGroupChannels();
  }

  // Single API call — returns all channels with EPG + HLS URL
  private loadGroupChannels(): void {
    this.fastService.getGroupChannels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const raw: any[] = Array.isArray(res) ? res : (res?.data || []);
          // Attach group_logo and parse EPG for every channel upfront
          this.channels = raw.map(ch => this.processChannel(ch));
          this.refreshProgress(); // compute stable progressPct + minsLeft immediately
          if (this.channels.length) {
            this.selectChannel(this.channels[0]);
          }
        },
        error: (err: any) => console.error('[FastChannel] Failed to load channels:', err)
      });
  }

  // Enrich raw API channel with derived EPG slots
  private processChannel(raw: any): any {
    const programs = this.derivePrograms(raw.epg_list || []);

    return {
      ...raw,
      logo: raw.group_logo || this.networkLogo,
      nowPlaying:   programs.now,
      nextProgram:  programs.next,
      laterProgram: programs.later,
    };
  }

  // Derive NOW / NEXT / LATER from epg_list
  private derivePrograms(epgList: any[]): { now: any, next: any, later: any } {
    const now = new Date();
    const sorted = epgList
      .map(item => ({
        title:      item.title,
        desc:       item.description,
        start_time: new Date(item.start),
        end_time:   new Date(item.end)
      }))
      .filter(p => p.start_time && p.end_time)
      .sort((a, b) => a.start_time.getTime() - b.start_time.getTime());

    let base = sorted.findIndex(p => now >= p.start_time && now < p.end_time);
    if (base === -1) base = sorted.findIndex(p => now < p.start_time);
    if (base === -1) base = 0;

    return {
      now:   sorted[base]     || null,
      next:  sorted[base + 1] || null,
      later: sorted[base + 2] || null,
    };
  }

  selectChannel(channel: any): void {
    this.activeChannel = channel;
    const hlsUrl = channel.channels?.[0]?.hls_url || '';
    this.initPlayer(hlsUrl);

    // Schedule a refresh when the current program ends
    const endTime = channel.playingInfo?.current_item_end_time;
    if (endTime) this.scheduleRefresh(endTime);
  }

  // Compute progress % and minsLeft as stable values — stored on nowPlaying
  // Called once after channels load and every 30 s via progressTimer
  private refreshProgress(): void {
    const now = Date.now();
    this.channels.forEach(ch => {
      const p = ch.nowPlaying;
      if (!p?.start_time || !p?.end_time) return;
      const start = p.start_time.getTime();
      const end   = p.end_time.getTime();
      p.progressPct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
      const msLeft  = end - now;
      const mins    = msLeft > 0 ? Math.ceil(msLeft / 60000) : 0;
      p.minsLeft    = mins > 0 ? `${mins} min${mins !== 1 ? 's' : ''} left` : '0 mins left';
    });
  }

  // Fires once when current program ends → re-fetch to refresh EPG
  private scheduleRefresh(endTime: string): void {
    if (this.epgReloadTimer) clearTimeout(this.epgReloadTimer);
    const ms = new Date(endTime).getTime() - Date.now();
    if (ms > 0) {
      this.epgReloadTimer = setTimeout(() => {
        this.fastService.getGroupChannels()
          .pipe(takeUntil(this.destroy$))
          .subscribe((res: any) => {
            const raw: any[] = Array.isArray(res) ? res : (res?.data || []);
            this.channels = raw.map(ch => this.processChannel(ch));
            const updated = this.channels.find(ch => ch.id === this.activeChannel?.id);
            if (updated) this.activeChannel = updated;
          });
      }, ms + 1000);
    }
  }

  private initPlayer(src: string): void {
    if (!src) return;
    if (this.player?.destroy) {
      this.player.destroy();
      this.player = null;
    }
    if ((window as any).StormcloudVP?.StormcloudVideoPlayer) {
      this.createPlayer(src);
    } else {
      this.loadStormcloudScript(src);
    }
  }

  private createPlayer(src: string): void {
    const { StormcloudVideoPlayer } = (window as any).StormcloudVP;
    const videoEl = this.videoElement.nativeElement;
    const adContainer = videoEl.parentElement || videoEl;
    this.player = new StormcloudVideoPlayer({
      videoElement: videoEl,
      adContainer,
      src,
      autoplay: true,
      muted: false,
      controls: this.controls,
      // playsinline: true,
      debug: false,
      debugAdTiming: false,
      showCustomControls: true,
      allowNativeHls: true,
      disableTracking: true,
      enableTracking: false,
      tracking: false,
      adPlayerType: 'ima',
      licenseKey: 'bigbee112',
    });
    this.player.load();
  }

  private loadStormcloudScript(src: string): void {
    const existingScript = document.querySelector('script[src*="stormcloud"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if ((window as any).StormcloudVP?.StormcloudVideoPlayer) {
          this.createPlayer(src);
        }
      });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/stormcloud-video-player@0.3.63/dist/stormcloud-vp.min.js';
    script.onload = () => {
      if ((window as any).StormcloudVP?.StormcloudVideoPlayer) {
        this.createPlayer(src);
      } else {
        console.error('Stormcloud Video Player script loaded but not properly initialized');
      }
    };
    script.onerror = () => {
      console.error('Failed to load Stormcloud Video Player script');
    };
    document.head.appendChild(script);
  }

  private clearRefreshTimer(): void {
    if (this.epgReloadTimer) {
      clearTimeout(this.epgReloadTimer);
      this.epgReloadTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.player?.destroy) this.player.destroy();
    this.clearRefreshTimer();
    if (this.progressTimer) clearInterval(this.progressTimer);
  }
}